import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SortableList, { DragHandle } from "../../components/SortableList";

const BLANK = {
  slug: "", name_tr: "", name_en: "", name_de: "", description: "", image: "",
  checklist: [], seo_title: "", seo_description: "", order: 0, active: true,
};

export default function AdminRoomTypes() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get("/admin/room-types").then((r) => setRows(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, order: Number(form.order || 0) };
    try {
      if (editingId) await api.put(`/admin/room-types/${editingId}`, body);
      else await api.post("/admin/room-types", body);
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/room-types/${id}`);
    toast.success("Silindi"); load();
  };

  const persistReorder = async (next) => {
    setRows(next);
    try {
      await api.put(`/admin/room-types/_reorder`, { ids: next.map((r) => r.id) });
      toast.success("Sıralama güncellendi");
    } catch { toast.error("Sıra kaydedilemedi"); load(); }
  };

  const addChecklistItem = () => setForm({
    ...form,
    checklist: [...(form.checklist || []), { product_id: "", product_name: "", quantity: 1, required: true, star_note: "" }],
  });
  const updateCI = (i, k, v) => setForm({
    ...form,
    checklist: form.checklist.map((c, idx) => idx === i ? { ...c, [k]: v } : c),
  });
  const removeCI = (i) => setForm({ ...form, checklist: form.checklist.filter((_, idx) => idx !== i) });

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-rooms">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-serif text-3xl font-semibold text-navy">Oda Tipleri</h1>
        <Button onClick={() => { setForm(BLANK); setEditingId(null); setOpen(true); }} className="bg-gold hover:bg-[#c49d58]">
          <Plus size={16} className="mr-1" /> Yeni Oda Tipi
        </Button>
      </div>

      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <div className="bg-softbg text-xs uppercase tracking-wider text-gray-500 px-3 py-3 grid grid-cols-[auto_1.4fr_1.4fr_100px_80px_80px_120px] gap-3 items-center">
          <span></span><span>Oda Tipi</span><span>Slug</span><span className="text-center">Checklist</span><span className="text-center">Sıra</span><span className="text-center">Aktif</span><span className="text-right">İşlem</span>
        </div>
        <div className="p-2">
          <SortableList items={rows} setItems={persistReorder} keyOf={(r) => r.id}>
            {(r, dragProps) => (
              <div className="grid grid-cols-[auto_1.4fr_1.4fr_100px_80px_80px_120px] gap-3 items-center bg-white border border-gray-100 rounded-md px-3 py-2 mb-2 hover:bg-softbg/60" data-testid={`admin-room-row-${r.slug}`}>
                <DragHandle {...dragProps} />
                <div className="flex items-center gap-3">
                  {r.image && <img src={r.image} alt="" className="h-8 w-8 rounded object-cover" />}
                  <span className="font-medium text-navy text-sm">{r.name_tr}</span>
                </div>
                <span className="text-gray-500 text-xs">/{r.slug}</span>
                <span className="text-center text-sm">{(r.checklist || []).length}</span>
                <span className="text-center text-sm">{r.order}</span>
                <span className="text-center">{r.active ? "●" : "○"}</span>
                <div className="text-right">
                  <button onClick={() => { setForm({ ...BLANK, ...r }); setEditingId(r.id); setOpen(true); }} className="p-1.5 text-gray-500 hover:text-teal" data-testid={`admin-room-edit-${r.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => del(r.id)} className="p-1.5 text-gray-500 hover:text-rose-600" data-testid={`admin-room-delete-${r.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto admin-scroll">
          <DialogHeader><DialogTitle className="heading-serif">{editingId ? "Oda Tipi Düzenle" : "Yeni Oda Tipi"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required /></div>
              <div><Label className="text-xs">Ad (TR)</Label><Input value={form.name_tr} onChange={(e) => setForm({ ...form, name_tr: e.target.value })} required /></div>
            </div>
            <div><Label className="text-xs">Açıklama</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <ImageUpload value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Görsel" testId="room-image" />
              <div><Label className="text-xs">Sıra</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
            </div>

            <div className="pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Ekipman Checklist</Label>
                <Button type="button" size="sm" variant="outline" onClick={addChecklistItem}><Plus size={12} className="mr-1" /> Ekle</Button>
              </div>
              <div className="space-y-2">
                {(form.checklist || []).map((c, i) => (
                  <div key={i} className="bg-softbg rounded p-3 grid grid-cols-[2fr_2fr_1fr_1.5fr_auto] gap-2 items-center text-xs">
                    <select className="border rounded h-9 px-2 bg-white" value={c.product_id} onChange={(e) => {
                      const p = products.find((p) => p.id === e.target.value);
                      updateCI(i, "product_id", e.target.value);
                      if (p) updateCI(i, "product_name", p.name);
                    }}>
                      <option value="">Ürün seç...</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <Input className="h-9" value={c.product_name} onChange={(e) => updateCI(i, "product_name", e.target.value)} placeholder="Ürün adı" />
                    <Input className="h-9" type="number" value={c.quantity} onChange={(e) => updateCI(i, "quantity", Number(e.target.value))} />
                    <Input className="h-9" value={c.star_note} onChange={(e) => updateCI(i, "star_note", e.target.value)} placeholder="Yıldız notu" />
                    <div className="flex items-center gap-1">
                      <label className="flex items-center gap-1"><input type="checkbox" checked={c.required} onChange={(e) => updateCI(i, "required", e.target.checked)} /> Zrl</label>
                      <button type="button" onClick={() => removeCI(i)} className="text-rose-600"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif</label>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-gold hover:bg-[#c49d58]">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
